<!--
  File name: Reports.vue
  Program Description:
    - View page for PDBNF Reports
    - Contains bar graph and pie graph for Total no. of DBN per General Cause and Total no. of DBN per Sector
-->
<template>
  <v-container flex>
    <v-row>
      <v-col>
        <h1>DBN Reports</h1>
      </v-col>
      <v-col
        cols="2"
        class="d-flex justify-center"
      >
        <ExportDropdown
          :dataset="[sectorGraphData, causeGraphData, subsectorGraphData]"
          :chart-format="[sectorIsPie, genCauseIsPie]"
          :filtered-data="filteredDbns"
          :disabled="!filteredDbns.length"
          :sector-toggle-value="sectorToggleValue"
        />
      </v-col>
    </v-row>
    <v-row>
      <v-col cols="8">
        <v-card
          v-if="!isExternal"
          class="pa-4 mb-4"
        >
          <v-row
            class="pb-2"
            justify="space-between"
          >
            <v-card-title>
              Total no. of DBN per Sector
            </v-card-title>
            <v-card-title>
              <v-btn-toggle
                v-model="sectorToggleButton"
                mandatory
                multiple
                dense
              >
                <v-btn>
                  Sector
                </v-btn>
                <v-btn v-show="sector.length > 0">
                  Subsector
                </v-btn>
              </v-btn-toggle>
              <v-btn-toggle
                v-model="sectorToggle"
                mandatory
                dense
                class="ml-4"
              >
                <v-btn>
                  <v-icon>bar_chart</v-icon>
                </v-btn>
                <v-btn>
                  <v-icon>pie_chart</v-icon>
                </v-btn>
              </v-btn-toggle>
            </v-card-title>
          </v-row>
          <template v-if="sectorToggleButton.includes(0)">
            <template v-if="filteredDbns.length">
              <Chart
                v-if="sectorToggle === 0"
                chart-type="bar"
                :chart-data="sectorGraphData"
                :chart-options="graphOptions.barOptions"
                canvas-height="600px"
                label="Sector"
              />
              <Chart
                v-else
                chart-type="pie"
                :chart-data="sectorGraphData"
                :chart-options="graphOptions.pieOptions"
                canvas-height="400px"
                label="Sector"
              />
            </template>
            <template v-else>
              <h2
                v-if="!loading"
                class="text-center grey--text text-h6 my-4"
              >
                Sector
              </h2>
              <NoDataYet
                v-show="!loading"
                :image="implem"
                message="No data yet"
              />
              <div
                class="d-flex justify-center align-center"
                :style="loading ? 'min-height: 500px' : ''"
              >
                <v-progress-circular
                  v-show="loading"
                  indeterminate
                  color="primary"
                />
              </div>
            </template>
          </template>
          <template v-if="sectorToggleButton.includes(1) && sector.length > 0">
            <Chart
              v-if="filteredDbns.length && showSubsector"
              :chart-type="sectorToggle === 0 ? 'bar' : 'pie'"
              :chart-data="subsectorGraphData"
              :chart-options="sectorToggle === 0 ? graphOptions.barOptions : graphOptions.pieOptions"
              :canvas-height="sectorToggle === 0 ? '600px' : '400px'"
              label="Subsector"
            />
            <template v-else>
              <h2
                v-if="!loading"
                class="text-center grey--text text-h6 my-4"
              >
                Subsector
              </h2>
              <NoDataYet
                v-show="!loading"
                :image="implem"
                message="No data yet"
              />
              <div
                class="d-flex justify-center align-center"
                :style="loading ? 'min-height: 500px' : ''"
              >
                <v-progress-circular
                  v-show="loading"
                  indeterminate
                  color="primary"
                />
              </div>
            </template>
          </template>
        </v-card>
        <v-card class="pa-4">
          <v-row
            class="pb-2"
            justify="space-between"
          >
            <v-card-title>
              Total no. of DBN per General Cause
            </v-card-title>
            <v-card-title>
              <v-btn-toggle
                v-model="generalToggle"
                mandatory
                dense
              >
                <v-btn>
                  <v-icon>bar_chart</v-icon>
                </v-btn>
                <v-btn>
                  <v-icon>pie_chart</v-icon>
                </v-btn>
              </v-btn-toggle>
            </v-card-title>
          </v-row>
          <template v-if="filteredDbns.length">
            <Chart
              v-if="generalToggle === 0"
              chart-type="bar"
              :chart-data="causeGraphData"
              :chart-options="graphOptions.barOptions"
              canvas-height="600px"
            />
            <Chart
              v-else
              chart-type="pie"
              :chart-data="causeGraphData"
              :chart-options="graphOptions.pieOptions"
              canvas-height="400px"
            />
          </template>
          <template v-else>
            <NoDataYet
              v-show="!loading"
              :image="implem"
              message="No data yet"
            />
            <div
              class="d-flex justify-center align-center"
              :style="loading ? 'min-height: 500px' : ''"
            >
              <v-progress-circular
                v-show="loading"
                indeterminate
                color="primary"
              />
            </div>
          </template>
        </v-card>
      </v-col>
      <v-col cols="4">
        <Filters />
      </v-col>
    </v-row>
    <div
      v-show="!!tooltip"
      :style="{
        position: 'absolute',
        ...tooltipStyle,
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        padding: '10px'
      }"
    >
      <span style="color: white">{{ tooltip }}</span>
    </div>
  </v-container>
</template>

<script>
import implem from '@/assets/implem.png'
import Chart from '@/components/exports/charts/Chart.vue'
import Filters from '@/components/exports/charts/Filters.vue'
import crud from '@/utils/rest/crud'
import pdbnfGraph from '@/store/pdbnfGraph'
import { mapFields } from 'vuex-map-fields'
import { toDateString } from '@/utils/helpers'
import graphOptions from '@/meta/graphOptions'
import ExportDropdown from '@/components/exports/ExportDropdown'

export default {
  name: 'Reports',
  components: {
    ExportDropdown,
    Chart,
    Filters
  },
  data: () => ({
    loading: false,
    pdbnfList: [],
    sectorToggle: 0,
    generalToggle: 0,
    tooltip: '',
    sectorToggleButton: [0, 1],
    tooltipStyle: {
      left: 0,
      top: 0
    }
  }),
  computed: {
    ...mapFields('pdbnfGraph', [
      'timeFrame',
      'year',
      'month',
      'quarter',
      'sector',
      'subsector',
      'generalCause',
      'specificCause',
      'recordType',
      'notification',
      'classification',
      'dbnStatus',
      'sectorList',
      'causeList',
      'recordList',
      'yearItems',
      'statusList',
      'documentType'
    ]),
    graphOptions () {
      return graphOptions('pdbnf', this)
    },
    isExternal () {
      return this.$s.userType === 'External'
    },
    genCauseIsPie () {
      return {
        type: 'general cause',
        isPie: this.generalToggle === 1
      }
    },
    sectorIsPie () {
      return {
        type: 'sector',
        isPie: this.sectorToggle === 1
      }
    },
    showSubsector () {
      return this.subsectorGraphData.labels.length > 0
    },
    subsectorGraphData () {
      const dbnPerSubsector = {}
      if (!this.filteredDbns) return []
      for (const pdbnf of this.filteredDbns) {
        const subsectorName = this.subsectorName(pdbnf.pic.subsector)
        if (!subsectorName) continue
        const prevTotal = parseInt(dbnPerSubsector[`${subsectorName}`] || 0)
        dbnPerSubsector[`${subsectorName}`] = prevTotal + 1
      }
      const labels = Object.keys(dbnPerSubsector)
      const data = Object.values(dbnPerSubsector)
      return this.convertToChartData(data, labels)
    },
    sectorGraphData () {
      const sectorObjArr = {}
      // fill sectorObjArr of ids of sectors
      this.sectors.forEach(sec => {
        sectorObjArr[sec._id] = 0
      })
      // input number of pdbnfs per sector
      this.filteredDbns.forEach((pdbnf) => {
        sectorObjArr[pdbnf.pic.sector] += 1
      })

      const data = Object.values(sectorObjArr)
      const datasets = [
        {
          label: 'Sectors',
          data,
          fill: false
        }
      ]
      const labels = this.sectorNames
      const chartData = {
        labels,
        datasets,
        extraData: { labels }
      }
      return chartData
    },
    causeGraphData () {
      const genCauseObjArr = {}
      // fill genCauseObjArr of ids of general cause
      this.generalCauses.forEach(cause => {
        genCauseObjArr[cause._id] = 0
      })
      // input number of pdbnfs per general cause
      this.filteredDbns.forEach((pdbnf) => {
        genCauseObjArr[pdbnf.generalCause] += 1
      })

      const data = Object.values(genCauseObjArr)
      const datasets = [
        {
          label: 'General Causes',
          data,
          fill: false
        }
      ]
      const labels = this.generalCauseNames
      const chartData = {
        labels,
        datasets,
        extraData: { labels }
      }
      return chartData
    },

    filteredDbns () {
      let filteredData = []
      filteredData = this.pdbnfList.filter((dbn) => {
        return this.filterNotif(dbn.notification) &&
        this.filterClassification(dbn.pic.sector) &&
          this.filterDocumentType(dbn.documentType) &&
       (this.yearArr && this.filterYear(dbn.year)) &&
       (this.quarterArr && this.filterQuarter(dbn.dateOfNotification)) &&
       (this.monthArr && this.filterMonth(dbn.dateOfNotification)) &&
       (this.recordArr && this.filterRecord(dbn.recordType)) &&
         (this.statusArr && this.filterStatus(dbn.status._id)) &&
         (this.genCauseArr && this.filterCause(dbn.generalCause)) &&
         (this.specCauseArr && this.filterSpecCause(dbn.specificCause, dbn.specificCauseOthers)) &&
        (this.sectorArr && this.filterSector(dbn.pic.sector)) &&
        (this.subsectorArr && this.filterSubsector(dbn.pic.subsector))
      })
      return filteredData
    },
    yearArr () {
      if (this.year) {
        return this.year.map((i) => { return i })
      }
      return true
    },
    quarterArr () {
      if (this.quarter) {
        const quarterMonths = []
        this.quarter.forEach((i) => {
          if (i === '1st Qtr') {
            quarterMonths.push('January', 'February', 'March')
          }
          if (i === '2nd Qtr') {
            quarterMonths.push('April', 'May', 'June')
          }
          if (i === '3rd Qtr') {
            quarterMonths.push('July', 'August', 'September')
          }
          if (i === '4th Qtr') {
            quarterMonths.push('October', 'November', 'December')
          }
        })
        return quarterMonths
      }
      return true
    },
    monthArr () {
      if (this.month) {
        return this.month.map((i) => { return i })
      }
      return true
    },
    statusArr () {
      if (this.dbnStatus) {
        return this.dbnStatus.map((i) => { return i })
      }
      return true
    },
    sectorArr () {
      if (this.sector) {
        return this.sector.map((i) => { return i })
      }
      return true
    },
    subsectorArr () {
      if (this.subsector) {
        return this.subsector.map((i) => { return i })
      }
      return true
    },
    genCauseArr () {
      if (this.generalCause) {
        return this.generalCause.map((i) => { return i })
      }
      return true
    },
    specCauseArr () {
      if (this.specificCause) {
        return this.specificCause.map((i) => { return i })
      }
      return true
    },
    recordArr () {
      if (this.recordType) {
        return this.recordType.map((i) => { return i._id })
      }
      return true
    },
    sectors () {
      let sectors = []
      if (this.sectorList.length > 0) {
        sectors = this.sectorList.filter(x => x.type === 'sector')
      }
      return sectors
    },
    sectorNames () {
      let sectors = []
      if (this.sectors.length > 0) {
        sectors = this.sectors.map(x => x.value)
      }
      return sectors
    },
    subsectorName () {
      const subsectors = this.sectorList.filter(x => x.type === 'subsector')
      return _id => {
        const subsector = subsectors.find(item => item._id === _id)
        return subsector && subsector.value
      }
    },
    generalCauses () {
      let generalCauses = []
      if (this.causeList.length > 0) {
        generalCauses = this.causeList.filter(x => x.type === 'general')
      }
      return generalCauses
    },
    generalCauseNames () {
      let generalCauseNames = []
      if (this.generalCauses.length > 0) {
        generalCauseNames = this.generalCauses.map(x => x.value)
      }
      return generalCauseNames
    },
    sectorToggleValue () {
      if (!this.sector.length) return [0]
      return this.sectorToggleButton
    },
    implem () {
      return implem
    }
  },
  watch: {
    sector (val) {
      if (!val.length) {
        this.subsector = []
      }
    },
    generalCause (val) {
      if (!val.length) {
        this.specificCause = []
      }
    },
    timeFrame () {
      this.year = []
      this.month = []
      this.quarter = []
    }
  },
  async created () {
    this.loading = true
    await Promise.all([
      this.getPdbnf(),
      this.getSectors(),
      this.getCauseOfIncidents(),
      this.getStatuses()
    ])
    this.getYears()
    this.loading = false
  },
  beforeCreate () {
    if (!this.$store.hasModule('pdbnfGraph')) {
      this.$store.registerModule('pdbnfGraph', pdbnfGraph)
    }
  },
  beforeDestroy () {
    this.$store.dispatch('pdbnfGraph/reset')
  },
  methods: {
    convertToChartData (data, labels, extra) {
      const maxBarThickness = 70
      const fill = false
      const backgroundColor = this.graphOptions.graphColors
      const datasets = [{ data, fill, maxBarThickness, backgroundColor }]
      const extraData = extra || { labels }
      return { datasets, labels, extraData }
    },
    // Get current submitted pdbnfs
    async getPdbnf () {
      const advancedQuery = { submitted: true }
      const pdbnfList = await crud('graph/pdbnf').list({ advancedQuery })
      pdbnfList.entries && this.$set(this.$data, 'pdbnfList', pdbnfList.entries)
    },
    // Get all possible years from pdbnf list
    getYears () {
      this.pdbnfList.forEach((dbn) => {
        if (!this.yearItems.includes(dbn.year)) {
          this.yearItems.push(dbn.year)
        }
      })
    },
    // Get current sectors from master data
    async getSectors () {
      const sectorList = await crud('sector').list({ count: 1000 })
      if (sectorList.entries) {
        this.sectorList = sectorList.entries
      }
    },
    // Get pdbnf statuses from master data
    async getStatuses () {
      const advancedQuery = { value: { $ne: 'Drafted' } }
      const statusList = await crud('dbnStatus').list({ count: 1000, advancedQuery })
      if (statusList.entries) {
        this.statusList = statusList.entries
      }
    },
    // Get cause of incidents from master data
    async getCauseOfIncidents () {
      const causeOfIncidentList = await crud('causeOfIncident').list({ count: 1000 })
      if (causeOfIncidentList.entries) {
        this.causeList = causeOfIncidentList.entries
      }
    },
    // Get classification of sector prop
    checkClassification (sector) {
      const currSector = this.sectorList.find((i) => {
        return i._id === sector
      })
      return currSector.classification
    },
    // returns true if year prop is within this.yearArr
    filterYear (year) {
      if (this.yearArr.length) {
        return this.yearArr.includes(year)
      }
      return true
    },
    // returns true if creation month is within filtered quarter
    filterQuarter (dateOfNotification) {
      const date = toDateString(dateOfNotification)
      if (this.quarterArr.length) {
        return this.quarterArr.some((month) => {
          return date.includes(month)
        })
      }
      return true
    },
    // returns true if creation month is within filtered month
    filterMonth (dateOfNotification) {
      const date = toDateString(dateOfNotification)
      if (this.monthArr.length) {
        return this.monthArr.some((month) => {
          return date.includes(month)
        })
      }
      return true
    },
    // returns true if status is selected for status filter
    filterStatus (statusId) {
      if (this.statusArr.length) {
        return this.statusArr.includes(statusId)
      }
      return true
    },
    // returns true when notif prop is equal to notification filter
    filterNotif (notif) {
      if (this.notification) {
        return notif === this.notification
      }
      return true
    },
    // returns true when sector prop has the same classification as selected in classification filter
    filterClassification (sector) {
      if (this.classification) {
        const classification = this.checkClassification(sector)
        return classification === this.classification
      }
      return true
    },
    // returns true if docType is same as selected in document type filter
    filterDocumentType (docType) {
      const doc = docType === 'PDBNF' ? 'PDBN' : docType
      if (this.documentType.length) {
        return this.documentType.includes(doc)
      }
      return true
    },
    // returns true when record prop is equal to record filter
    filterRecord (record) {
      if (this.recordArr.length) {
        return this.recordArr.includes(record)
      }
      return true
    },
    // returns true when sector prop is included in selected filter
    filterSector (sector) {
      if (this.sectorArr.length) {
        return this.sectorArr.includes(sector)
      }
      return true
    },
    // returns true when subsector prop is included in selected filter
    filterSubsector (subsector) {
      if (this.subsectorArr.length) {
        return this.subsectorArr.includes(subsector)
      }
      return true
    },
    // returns true when genCause prop is included in selected filter
    filterCause (genCause) {
      if (this.genCauseArr.length) {
        return this.genCauseArr.includes(genCause)
      }
      return true
    },
    // returns true when specCause prop is selected in filter
    filterSpecCause (specCause, others) {
      if (specCause === null && !others) {
        specCause = 'Others'
      }
      if (this.specCauseArr.length) {
        return this.specCauseArr.includes(specCause)
      }
      return true
    }
  }
}
</script>

<style scoped>
  .page {
    background: white;
  }
  p {
    font-size: 24px;
  }
</style>
